/* eslint-disable */
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import PdfPage from "../../pages/PdfPage";

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getPdfUrl(data = {}) {
  return (
    data.pdfUrl ||
    data.signedUrl ||
    data.downloadUrl ||
    data.fileUrl ||
    data.url ||
    data.previewUrl ||
    ""
  );
}

function getStoragePath(data = {}) {
  return (
    data.storagePath ||
    data.filePath ||
    data.path ||
    data.pdfPath ||
    data.storageRef ||
    ""
  );
}

function isTemporaryPdfUrlStillValid(row) {
  const pdfUrl = String(row?.pdfUrl || "").trim();
  if (!pdfUrl) return false;

  const expiresAt = toDate(row?.pdfUrlExpiresAt || row?.raw?.pdfUrlExpiresAt);
  if (!expiresAt) return false;

  return Date.now() < expiresAt.getTime();
}

async function getFreshReportUrl({ schoolId, studentId, monthKey, fallbackPdfUrl }) {
  if (schoolId && studentId && monthKey && functions) {
    const callable = httpsCallable(functions, "getMonthlyAttendanceReportUrl");
    const res = await callable({ schoolId, studentId, monthKey });

    const freshUrl = res?.data?.url;
    if (freshUrl) return freshUrl;
  }

  if (fallbackPdfUrl) return fallbackPdfUrl;

  throw new Error("No secure report link was returned.");
}

function getStudentIds(child = {}) {
  return Array.from(
    new Set(
      [
        child?.id,
        child?.studentId,
        child?.docId,
        child?.studentDocId,
        child?.childId,
      ]
        .filter(Boolean)
        .map(String)
    )
  );
}

function isLockedRow(row, overrideTestMode) {
  if (overrideTestMode) return false;

  const now = new Date();
  const releaseAt = toDate(row.releaseAt);
  const expiresAt = toDate(row.expiresAt);

  const beforeRelease = releaseAt ? now < releaseAt : false;
  const afterExpiry = expiresAt ? now > expiresAt : false;

  /*
    storagePath is the production source. pdfUrl is only a short-lived fallback
    and must not be trusted after pdfUrlExpiresAt.
  */
  const hasPdfSource = !!row.storagePath || isTemporaryPdfUrlStillValid(row);
  const downloadEnabled =
    typeof row.downloadEnabled === "boolean" ? row.downloadEnabled : hasPdfSource;

  return beforeRelease || afterExpiry || !downloadEnabled || !hasPdfSource;
}

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

const ParentMonthlyReportsSection = forwardRef(function ParentMonthlyReportsSection(
  {
    schoolId,
    selectedChild,
    overrideTestMode = false,
    title = "Monthly Attendance Reports",
    isOnline = true,
    offlineMessage = "You are offline. Please reconnect and try again.",
  },
  ref
) {
  const [rows, setRows] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const studentIds = useMemo(() => getStudentIds(selectedChild), [selectedChild]);
  const primaryStudentId = studentIds[0] || "";

  useEffect(() => {
    let alive = true;

    async function loadReports() {
      if (!schoolId || !studentIds.length) {
        setRows([]);
        setCheckedIds(studentIds);
        setLoading(false);
        setError("");
        return;
      }

      try {
        setLoading(true);
        setError("");
        setCheckedIds(studentIds);

        const found = new Map();

        for (const studentId of studentIds) {
          const reportsRef = collection(
            db,
            "schools",
            schoolId,
            "students",
            studentId,
            "monthlyReports"
          );

          const snap = await getDocs(reportsRef);

          snap.docs.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const key = `${studentId}-${docSnap.id}`;

            found.set(key, {
              id: docSnap.id,
              sourceStudentId: studentId,
              monthKey: data.monthKey || data.month || docSnap.id,
              monthLabel:
                data.monthLabel ||
                data.monthName ||
                data.label ||
                data.month ||
                data.monthKey ||
                docSnap.id,
              status: data.status || "ready",
              pdfUrl: getPdfUrl(data),
              pdfUrlExpiresAt: data.pdfUrlExpiresAt || null,
              storagePath: getStoragePath(data),
              generatedAt:
                data.generatedAt ||
                data.createdAt ||
                data.updatedAt ||
                null,
              releaseAt: data.releaseAt || data.availableFrom || null,
              expiresAt: data.expiresAt || data.expires || null,
              summary: data.summary || {
                attendanceRate:
                  data.attendanceRate ||
                  data.attendancePercentage ||
                  data.percentage ||
                  0,
                present: data.present || data.presentCount || 0,
                late: data.late || data.lateCount || 0,
                absent: data.absent || data.absentCount || 0,
              },
              downloadEnabled: data.downloadEnabled,
              raw: data,
            });
          });
        }

        const nextRows = Array.from(found.values()).sort((a, b) => {
          const aTime =
            toDate(a.generatedAt)?.getTime() ||
            toDate(a.raw?.createdAt)?.getTime() ||
            0;

          const bTime =
            toDate(b.generatedAt)?.getTime() ||
            toDate(b.raw?.createdAt)?.getTime() ||
            0;

          if (bTime !== aTime) return bTime - aTime;

          return String(b.monthKey || "").localeCompare(String(a.monthKey || ""));
        });

        if (!alive) return;
        setRows(nextRows);
      } catch (err) {
        console.error("Failed loading parent monthly reports:", err);
        if (!alive) return;
        setError("Failed to load monthly reports.");
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadReports();

    return () => {
      alive = false;
    };
  }, [schoolId, studentIds.join("|")]);

  const latestReport = useMemo(() => (rows.length ? rows[0] : null), [rows]);
  const olderReports = useMemo(() => rows.slice(1), [rows]);

  return (
    <section ref={ref} style={shell}>
      {!primaryStudentId ? (
        <StateCard title={title} message="Select a child to view monthly reports." />
      ) : loading ? (
        <StateCard title={title} message="Loading monthly reports…" />
      ) : error ? (
        <StateCard title={title} message={error} tone="error" />
      ) : !rows.length ? (
        <StateCard
          title={title}
          message="No monthly reports available yet."
          subtext={`Checked student record: ${checkedIds.join(", ") || "none"}`}
        />
      ) : (
        <>
          {latestReport ? (
            <LatestReportCard
              schoolId={schoolId}
              row={latestReport}
              overrideTestMode={overrideTestMode}
              title={title}
              isOnline={isOnline}
              offlineMessage={offlineMessage}
            />
          ) : null}

          {olderReports.length ? (
            <div style={historyCard}>
              <div style={historyHeader}>
                <div>
                  <div style={eyebrow}>Archive</div>
                  <div style={sectionTitle}>Older reports</div>
                </div>
                <div style={softCount}>{olderReports.length}</div>
              </div>

              <div style={listWrap}>
                {olderReports.map((row) => (
                  <HistoryRow
                    key={`${row.sourceStudentId}-${row.id}`}
                    schoolId={schoolId}
                    row={row}
                    overrideTestMode={overrideTestMode}
                    isOnline={isOnline}
                    offlineMessage={offlineMessage}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
});

function StateCard({ title, message, subtext, tone }) {
  return (
    <div style={heroReportCard}>
      <div style={heroGlow} />
      <div style={heroTop}>
        <div style={copyBlock}>
          <div style={eyebrow}>Reports</div>
          <div style={heroTitleSmall}>{title}</div>
          {subtext ? <div style={subtle}>{subtext}</div> : null}
        </div>
      </div>
      <div style={tone === "error" ? errorCard : emptyInlineCard}>{message}</div>
    </div>
  );
}

function LatestReportCard({ schoolId, row, overrideTestMode, title, isOnline, offlineMessage }) {
  const [busyAction, setBusyAction] = useState("");
  const [openError, setOpenError] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const locked = isLockedRow(row, overrideTestMode);
  const attendanceRate = row.summary?.attendanceRate ?? row.summary?.attendancePct ?? "—";
  const attendanceNumber = clampPercent(attendanceRate);

  async function openReport(mode) {
    if (locked || busyAction) return;

    if (!isOnline) {
      setOpenError(offlineMessage);
      return;
    }

    try {
      setBusyAction(mode);
      setOpenError("");

      const fallbackPdfUrl = isTemporaryPdfUrlStillValid(row) ? row.pdfUrl : "";

      const url = await getFreshReportUrl({
        schoolId,
        studentId: row.sourceStudentId,
        monthKey: row.monthKey || row.id,
        fallbackPdfUrl,
      });

      if (mode === "preview") {
        setPreviewUrl(url);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
  console.error("Could not open report:", err);

  const code = String(err?.code || "").toLowerCase();
  const message = String(err?.message || "");

  if (
    code.includes("unauthenticated") ||
    message.toLowerCase().includes("unauthenticated")
  ) {
    setOpenError("Please close and reopen the app, then try the report again.");
  } else {
    setOpenError(
      message || "Could not open this report. Please refresh and try again."
    );
  }
} finally {
  setBusyAction("");
}
  }

  return (
    <div style={heroReportCard}>
      <div style={heroGlow} />

      <div style={heroTop}>
        <div style={copyBlock}>
          <div style={eyebrow}>Reports</div>
          <div style={heroTitleSmall}>{title}</div>
          <div style={heroMonthTitle}>{row.monthLabel}</div>
          <div style={subtle}>Generated {formatDateTime(row.generatedAt)}</div>
        </div>

        <div style={{ ...statusPillBase, ...(locked ? lockedPill : readyPill) }}>
          {locked ? "Locked" : "Ready"}
        </div>
      </div>

      <div style={heroStatCard}>
        <div style={heroStatTop}>
          <div>
            <div style={heroStatLabel}>Attendance score</div>
            <div style={heroStatValue}>{attendanceRate}%</div>
          </div>

          <div style={ringWrap} aria-hidden="true">
            <div style={ringOuter}>
              <div
                style={{
                  ...ringFill,
                  background: `conic-gradient(#ffffff ${
                    attendanceNumber * 3.6
                  }deg, rgba(255,255,255,0.22) 0deg)`,
                }}
              />
              <div style={ringInner}>{attendanceNumber}%</div>
            </div>
          </div>
        </div>

        <div style={heroStatBarTrack}>
          <div style={{ ...heroStatBarFill, width: `${attendanceNumber}%` }} />
        </div>
      </div>

      <div style={summaryGrid}>
        <MetricTile label="Present" value={row.summary?.present ?? 0} />
        <MetricTile label="Late" value={row.summary?.late ?? 0} />
        <MetricTile label="Absent" value={row.summary?.absent ?? 0} />
      </div>

      <div style={metaGrid}>
        <div style={metaItemCard}>
          <span style={metaLabel}>Release</span>
          <strong>{formatDateTime(row.releaseAt)}</strong>
        </div>

        <div style={metaItemCard}>
          <span style={metaLabel}>Expires</span>
          <strong>{formatDateTime(row.expiresAt)}</strong>
        </div>
      </div>

      {locked ? (
        <div style={lockedExplanationBox}>
          Available after the school publishing window. Reports become available once
          approved and published by the school.
        </div>
      ) : null}

      {openError ? <div style={errorCard}>{openError}</div> : null}

      <div style={actionsRow}>
        {!locked ? (
          <>
            <button
              type="button"
              style={secondaryBtn}
              disabled={!!busyAction}
              onClick={() => openReport("preview")}
            >
              {busyAction === "preview" ? "Preparing preview..." : "Preview PDF"}
            </button>

            <button
              type="button"
              style={primaryBtn}
              disabled={!!busyAction}
              onClick={() => openReport("download")}
            >
              {busyAction === "download" ? "Opening..." : "Download PDF"}
            </button>
          </>
        ) : (
          <>
            <button type="button" style={disabledBtn} disabled>
              Preview unavailable
            </button>
            <button type="button" style={disabledBtn} disabled>
              Download unavailable
            </button>
          </>
        )}
      </div>

      {previewUrl ? (
        <PdfPage
          url={previewUrl}
          title={`${row.monthLabel} attendance report`}
          onClose={() => setPreviewUrl("")}
        />
      ) : null}
    </div>
  );
}

function MetricTile({ label, value }) {
  return (
    <div style={summaryTile}>
      <div style={summaryLabel}>{label}</div>
      <div style={summaryValue}>{value}</div>
    </div>
  );
}

function HistoryRow({ schoolId, row, overrideTestMode, isOnline, offlineMessage }) {
  const [busy, setBusy] = useState(false);
  const [openError, setOpenError] = useState("");

  const locked = isLockedRow(row, overrideTestMode);

  async function openReport() {
    if (locked || busy) return;

    if (!isOnline) {
      setOpenError(offlineMessage);
      return;
    }

    try {
      setBusy(true);
      setOpenError("");

      const fallbackPdfUrl = isTemporaryPdfUrlStillValid(row) ? row.pdfUrl : "";

      const url = await getFreshReportUrl({
        schoolId,
        studentId: row.sourceStudentId,
        monthKey: row.monthKey || row.id,
        fallbackPdfUrl,
      });

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
  console.error("Could not open report:", err);

  const code = String(err?.code || "").toLowerCase();
  const message = String(err?.message || "");

  if (
    code.includes("unauthenticated") ||
    message.toLowerCase().includes("unauthenticated")
  ) {
    setOpenError("Please close and reopen the app, then try the report again.");
  } else {
    setOpenError(
      message || "Could not open this report. Please refresh and try again."
    );
  }
} finally {
  setBusy(false);
}
  }

  return (
    <div style={listItem}>
      <div style={listMain}>
        <div style={listTitle}>{row.monthLabel}</div>
        <div style={listMeta}>Generated {formatDateTime(row.generatedAt)}</div>
        <div style={listMeta}>
          Release {formatDate(row.releaseAt)} • Expires {formatDate(row.expiresAt)}
        </div>
        {openError ? <div style={listError}>{openError}</div> : null}
      </div>

      <div style={listSide}>
        <span style={{ ...statusPillBase, ...(locked ? lockedPill : readyPill) }}>
          {locked ? "Locked" : "Ready"}
        </span>

        {!locked ? (
          <button type="button" onClick={openReport} disabled={busy} style={secondaryBtn}>
            {busy ? "Opening..." : "Download"}
          </button>
        ) : (
          <button type="button" style={miniDisabledBtn} disabled>
            Unavailable
          </button>
        )}
      </div>
    </div>
  );
}

export default ParentMonthlyReportsSection;

const shell = {
  display: "grid",
  gap: 16,
  width: "100%",
};

const heroReportCard = {
  position: "relative",
  overflow: "hidden",
  padding: 20,
  borderRadius: 28,
  background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
  border: "1px solid rgba(224, 232, 247, 0.96)",
  boxShadow:
    "0 18px 38px rgba(20, 54, 110, 0.09), 0 6px 14px rgba(20, 54, 110, 0.04)",
  display: "grid",
  gap: 16,
};

const heroGlow = {
  position: "absolute",
  width: 190,
  height: 190,
  right: -76,
  top: -90,
  borderRadius: 999,
  background:
    "radial-gradient(circle, rgba(45, 109, 242, 0.16), transparent 68%)",
  pointerEvents: "none",
};

const copyBlock = {
  minWidth: 0,
  position: "relative",
};

const heroTop = {
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
};

const eyebrow = {
  fontSize: 11,
  fontWeight: 950,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: "#7288ad",
  marginBottom: 7,
};

const heroTitleSmall = {
  maxWidth: "22ch",
  fontSize: 18,
  fontWeight: 950,
  color: "#183566",
  lineHeight: 1.22,
  letterSpacing: "-0.025em",
};

const heroMonthTitle = {
  fontSize: 30,
  fontWeight: 950,
  color: "#071633",
  marginTop: 12,
  lineHeight: 1,
  letterSpacing: "-0.045em",
};

const subtle = {
  marginTop: 8,
  color: "#7a8db2",
  fontSize: 13,
  lineHeight: 1.5,
};

const heroStatCard = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 24,
  padding: 18,
  background: "linear-gradient(145deg, #1659e8 0%, #327dfb 100%)",
  color: "#ffffff",
  boxShadow:
    "0 18px 32px rgba(43, 112, 241, 0.22), inset 0 1px 0 rgba(255,255,255,0.18)",
};

const heroStatTop = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
};

const heroStatLabel = {
  color: "rgba(255,255,255,0.82)",
  fontSize: 11,
  fontWeight: 950,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const heroStatValue = {
  marginTop: 8,
  color: "#ffffff",
  fontSize: 42,
  lineHeight: 0.95,
  fontWeight: 950,
  letterSpacing: "-0.05em",
};

const ringWrap = {
  flex: "0 0 auto",
};

const ringOuter = {
  width: 72,
  height: 72,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  position: "relative",
  background: "rgba(255,255,255,0.18)",
};

const ringFill = {
  position: "absolute",
  inset: 0,
  borderRadius: 999,
};

const ringInner = {
  position: "relative",
  width: 54,
  height: 54,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  background: "#236cf2",
  color: "#ffffff",
  fontSize: 13,
  fontWeight: 950,
};

const heroStatBarTrack = {
  marginTop: 16,
  height: 8,
  borderRadius: 999,
  overflow: "hidden",
  background: "rgba(255,255,255,0.22)",
};

const heroStatBarFill = {
  height: "100%",
  borderRadius: 999,
  background: "#ffffff",
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 10,
};

const summaryTile = {
  minWidth: 0,
  minHeight: 82,
  padding: "13px 10px",
  borderRadius: 18,
  background: "#f3f7ff",
  border: "1px solid #e5edfb",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  textAlign: "center",
};

const summaryLabel = {
  fontSize: 10.5,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#7180a6",
  fontWeight: 950,
  marginBottom: 8,
  lineHeight: 1.1,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};

const summaryValue = {
  fontSize: 28,
  fontWeight: 950,
  color: "#071633",
  lineHeight: 1,
};

const metaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 10,
};

const metaItemCard = {
  minWidth: 0,
  padding: "12px 13px",
  borderRadius: 16,
  background: "rgba(248, 251, 255, 0.9)",
  border: "1px solid #e8eefb",
};

const metaLabel = {
  display: "block",
  color: "#7a8db2",
  fontSize: 12,
  fontWeight: 900,
  marginBottom: 4,
};

const actionsRow = {
  display: "flex",
  justifyContent: "flex-start",
  gap: 10,
  flexWrap: "wrap",
};

const baseBtn = {
  minHeight: 46,
  borderRadius: 16,
  fontWeight: 950,
  fontSize: 14,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  padding: "0 16px",
  cursor: "pointer",
};

const primaryBtn = {
  ...baseBtn,
  border: "none",
  background: "linear-gradient(180deg, #2d6df2 0%, #4a88ff 100%)",
  color: "#ffffff",
  boxShadow: "0 12px 24px rgba(43, 112, 241, 0.18)",
};

const secondaryBtn = {
  ...baseBtn,
  background: "#edf4ff",
  color: "#2d61d2",
  border: "1px solid #dfe8fb",
};

const disabledBtn = {
  ...baseBtn,
  border: "1px solid #d8dee9",
  background: "#eef2f7",
  color: "#667085",
  cursor: "not-allowed",
  opacity: 0.72,
};

const emptyInlineCard = {
  position: "relative",
  padding: "16px 14px",
  borderRadius: 18,
  background: "#f8faff",
  color: "#5f6b92",
  border: "1px solid #e4eafb",
};

const errorCard = {
  position: "relative",
  padding: "14px 14px",
  borderRadius: 18,
  background: "#fff1f2",
  color: "#9f1239",
  border: "1px solid #fecdd3",
  fontWeight: 850,
};

const historyCard = {
  padding: 18,
  borderRadius: 26,
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  border: "1px solid rgba(224, 232, 247, 0.96)",
  boxShadow: "0 16px 34px rgba(20, 54, 110, 0.08)",
  display: "grid",
  gap: 14,
};

const historyHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const sectionTitle = {
  fontSize: 20,
  fontWeight: 950,
  color: "#183566",
  letterSpacing: "-0.03em",
};

const softCount = {
  minWidth: 36,
  height: 36,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  background: "#edf4ff",
  color: "#2d61d2",
  fontSize: 13,
  fontWeight: 950,
};

const listWrap = {
  display: "grid",
  gap: 12,
};

const listItem = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,
  padding: 15,
  borderRadius: 20,
  border: "1px solid #edf1fa",
  background: "#fbfcff",
};

const listMain = {
  minWidth: 0,
  display: "grid",
  gap: 4,
};

const listTitle = {
  fontSize: 16,
  fontWeight: 950,
  color: "#17203f",
};

const listMeta = {
  fontSize: 13,
  color: "#7a849f",
  lineHeight: 1.35,
};

const listError = {
  marginTop: 6,
  fontSize: 12,
  lineHeight: 1.4,
  color: "#b42318",
  fontWeight: 800,
};

const listSide = {
  flex: "0 0 auto",
  display: "grid",
  gap: 10,
  justifyItems: "end",
};

const lockedExplanationBox = {
  padding: "12px 14px",
  borderRadius: 16,
  background: "rgba(37, 99, 235, 0.08)",
  border: "1px solid rgba(37, 99, 235, 0.14)",
  color: "#475569",
  fontSize: 13,
  lineHeight: 1.55,
  fontWeight: 700,
};

const statusPillBase = {
  flex: "0 0 auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 34,
  padding: "0 12px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 950,
  textTransform: "capitalize",
  whiteSpace: "nowrap",
};

const readyPill = {
  background: "#DCFCE7",
  color: "#166534",
  border: "1px solid #BBF7D0",
};

const lockedPill = {
  background: "#FEF3C7",
  color: "#92400E",
  border: "1px solid #FDE68A",
};

const miniDisabledBtn = {
  border: "none",
  background: "#E5E7EB",
  color: "#6B7280",
  padding: "10px 12px",
  borderRadius: 12,
  fontWeight: 900,
  fontSize: 13,
};